import { useEffect, useState } from "react";
import apiClient from "../lib/api";
import StatCard from "../components/StatCard";
import {
  ChartLineUp, ChartBar, Table, Target, Trophy, Lightning, Percent,
} from "@phosphor-icons/react";
import AdminSection from "../components/admin/AdminSection";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Cell,
} from "recharts";

export default function Performance() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get("/stats/overview");
        setStats(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-8 pb-16">
        <div className="surface rounded-xl h-64 animate-pulse" />
      </div>
    );
  }

  if (!stats || !stats.overall) {
    return (
      <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-8 pb-16">
        <div className="mb-8">
          <div className="text-[11px] text-yellow-400 tracking-[0.25em] uppercase font-bold mb-2">Analíticas</div>
          <h1 className="text-display font-black text-3xl tracking-tighter leading-none">Récord</h1>
        </div>
        <div className="surface rounded-xl p-8 text-center">
          <p className="text-zinc-400 text-sm">Los resultados aparecerán a medida que se registren jugadas con resultado cerrado.</p>
        </div>
      </div>
    );
  }

  const { overall, by_sport, by_league, by_market, evolution } = stats;
  const hasData = evolution.length > 0 || overall.total_picks > 0;

  const sportData = Object.entries(by_sport).map(([k, v]) => ({ name: k, roi: v.roi, picks: v.total_picks }));
  const leagueData = Object.entries(by_league).map(([k, v]) => ({ name: k, roi: v.roi, picks: v.total_picks }));
  const marketData = Object.entries(by_market).map(([k, v]) => ({ name: k, roi: v.roi, picks: v.total_picks }));

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-8 pb-16">
      <div className="mb-8">
        <div className="text-[11px] text-yellow-400 tracking-[0.25em] uppercase font-bold mb-2">Analíticas</div>
        <h1 className="text-display font-black text-3xl lg:text-5xl tracking-tighter leading-none">Rendimiento</h1>
        <p className="text-zinc-400 mt-3 text-sm">
          {hasData
            ? "Toda la métrica que importa. Auditada sobre jugadas cerradas."
            : "Los resultados aparecerán a medida que se registren jugadas reales con resultado cerrado."}
        </p>
      </div>

      {/* Key stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
        <StatCard label="ROI" value={(overall.roi ?? 0).toFixed(2)} suffix="%" tone={(overall.roi ?? 0) >= 0 ? "positive" : "negative"} icon={ChartLineUp} testId="perf-stat-roi" />
        <StatCard label="Yield" value={(overall.yield_pct ?? 0).toFixed(2)} suffix="%" icon={Percent} testId="perf-stat-yield" />
        <StatCard label="Efectividad" value={(overall.win_rate ?? 0).toFixed(1)} suffix="%" icon={Target} testId="perf-stat-winrate" />
        <StatCard label="Profit" value={(overall.total_profit ?? 0).toFixed(2)} suffix="u" tone={(overall.total_profit ?? 0) >= 0 ? "positive" : "negative"} icon={Lightning} testId="perf-stat-profit" />
        <StatCard label="Total jugadas" value={overall.total_picks} icon={Trophy} testId="perf-stat-total" />
        <StatCard label="Cuota media" value={(overall.avg_odds ?? 0).toFixed(2)} testId="perf-stat-avg-odds" />
      </div>

      {/* Evolution */}
      <AdminSection icon={ChartLineUp} title="Profit acumulado" subtitle="Evolución en el tiempo" color="emerald" className="mb-6">
        <div className="h-[320px]" data-testid="perf-evolution-chart">
          {evolution.length > 0 ? (
            <ResponsiveContainer>
              <AreaChart data={evolution}>
                <defs>
                  <linearGradient id="prg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="date" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} width={40} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "8px", fontSize: "12px" }} />
                <Area type="monotone" dataKey="cumulative_profit" stroke="#10B981" strokeWidth={2} fill="url(#prg)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </div>
      </AdminSection>

      {/* Breakdown grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        <BreakdownChart title="Por deporte" data={sportData} />
        <BreakdownChart title="Por liga" data={leagueData} />
        <BreakdownChart title="Por mercado" data={marketData} />
      </div>

      {/* Tables */}
      <div className="mt-10 grid lg:grid-cols-2 gap-6">
        <BreakdownTable title="Rendimiento por deporte" data={by_sport} testIdPrefix="perf-tbl-sport" />
        <BreakdownTable title="Rendimiento por liga" data={by_league} testIdPrefix="perf-tbl-league" />
      </div>
    </div>
  );
}

function BreakdownChart({ title, data }) {
  if (!data || data.length === 0) {
    return (
      <AdminSection icon={ChartBar} title={title} subtitle="ROI por categoría" color="sky">
        <EmptyChart small />
      </AdminSection>
    );
  }
  return (
    <AdminSection icon={ChartBar} title={title} subtitle="ROI por categoría" color="sky">
      <div className="h-[200px]">
        <ResponsiveContainer>
          <BarChart data={data} layout="vertical">
            <XAxis type="number" axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} width={80} />
            <Tooltip contentStyle={{ background: "#121212", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "8px", fontSize: "12px" }} formatter={(v) => `${v.toFixed(2)}%`} />
            <Bar dataKey="roi" radius={[0, 4, 4, 0]}>
              {data.map((d, i) => (
                <Cell key={i} fill={d.roi >= 0 ? "#10B981" : "#EF4444"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </AdminSection>
  );
}

function BreakdownTable({ title, data, testIdPrefix }) {
  const rows = Object.entries(data || {});
  return (
    <AdminSection icon={Table} title={title} subtitle="Detalle por categoría" color="violet">
      {rows.length === 0 ? (
        <div className="text-zinc-500 text-sm py-8 text-center">Aún no hay datos</div>
      ) : (
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold border-b border-white/[0.06]">
                <th className="text-left py-2 font-semibold">Categoría</th>
                <th className="text-right py-2 font-semibold">Picks</th>
                <th className="text-right py-2 font-semibold">WR</th>
                <th className="text-right py-2 font-semibold">ROI</th>
                <th className="text-right py-2 font-semibold">Profit</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([name, s]) => (
                <tr key={name} className="border-b border-white/[0.04] last:border-0" data-testid={`${testIdPrefix}-${name}`}>
                  <td className="py-3 text-zinc-200 capitalize">{name}</td>
                  <td className="py-3 text-right font-mono">{s.total_picks}</td>
                  <td className="py-3 text-right font-mono">{Number(s.win_rate ?? 0).toFixed(1)}%</td>
                  <td className={`py-3 text-right font-mono font-bold ${(s.roi ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {(s.roi ?? 0) >= 0 ? "+" : ""}{Number(s.roi ?? 0).toFixed(2)}%
                  </td>
                  <td className={`py-3 text-right font-mono ${(s.total_profit ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {(s.total_profit ?? 0) >= 0 ? "+" : ""}{Number(s.total_profit ?? 0).toFixed(2)}u
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminSection>
  );
}

function EmptyChart({ small = false }) {
  return (
    <div className={`flex flex-col items-center justify-center text-center px-4 ${small ? "h-40" : "h-full"}`}>
      <div className="text-display font-bold text-base text-zinc-300">Sin datos todavía</div>
      <div className="text-xs text-zinc-500 mt-1.5 max-w-xs">Los resultados aparecerán al registrar jugadas cerradas.</div>
    </div>
  );
}
